import { Grid } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateOnly } from '~/lib/datetime';
import { formatMinutes, usePlayoutFilterParams } from '../lib';
import type { PlayoutReport__EventAggregate as EventAggregateRow } from '../queries/PlayoutReport.generated';
import { ByEventListCard } from './ByEventListCard';
import { LineChartCard } from './LineChartCard';

interface ByEventChartsProps {
  events: ReadonlyArray<EventAggregateRow>;
}

export const ByEventCharts = ({ events }: ByEventChartsProps) => {
  const { t } = useTranslation();
  const { setFilters } = usePlayoutFilterParams();

  const filterToEvent = useCallback(
    (eventId: number) => setFilters({ eventIds: [eventId] }, { push: true }),
    [setFilters],
  );
  const filterToEventAtIndex = useCallback(
    (dataIndex: number) => filterToEvent(events[dataIndex].eventId),
    [events, filterToEvent],
  );

  const eventDateLabels = useMemo(
    () =>
      events.map((e) =>
        e.eventStartTime
          ? formatDateOnly(e.eventStartTime, { showWeekday: false }) ?? e.event
          : e.event,
      ),
    [events],
  );
  const eventPlaysValues = useMemo(() => events.map((d) => d.plays), [events]);
  const eventDurationValues = useMemo(
    () => events.map((d) => formatMinutes(d.durationMs)),
    [events],
  );
  const playsTooltipFormatter = useCallback(
    (value: number | null, { dataIndex }: { dataIndex: number }) =>
      `${events[dataIndex]?.event ?? ''}: ${value?.toLocaleString()}`,
    [events],
  );
  const durationTooltipFormatter = useCallback(
    (value: number | null, { dataIndex }: { dataIndex: number }) =>
      `${events[dataIndex]?.event ?? ''}: ${value?.toLocaleString()} m`,
    [events],
  );

  if (events.length === 0) return null;

  const showCharts = events.length > 2;

  return (
    <>
      {showCharts && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <LineChartCard
              title={t('pages.playout_reports.charts.by_event_plays.title')}
              labels={eventDateLabels}
              values={eventPlaysValues}
              tooltip={t('pages.playout_reports.charts.by_event_plays.tooltip')}
              tooltipFormatter={playsTooltipFormatter}
              onPointClick={filterToEventAtIndex}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LineChartCard
              title={t('pages.playout_reports.charts.by_event_duration.title')}
              labels={eventDateLabels}
              values={eventDurationValues}
              tooltip={t('pages.playout_reports.charts.by_event_duration.tooltip')}
              tooltipFormatter={durationTooltipFormatter}
              onPointClick={filterToEventAtIndex}
            />
          </Grid>
        </Grid>
      )}
      <ByEventListCard rows={events} onEventClick={filterToEvent} />
    </>
  );
};
